import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Teacher: attendance for one course. Shows every joined participant with a
// checkbox for the chosen date (default: today). Checking/unchecking calls
// the API immediately.

const today = () => new Date().toISOString().slice(0, 10)

export function TeacherAttendancePage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const { user, loading: authLoading } = useAuth()
  const [course, setCourse] = useState(null)
  const [date, setDate] = useState(today())
  const [roster, setRoster] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)

  const allowed = user && (user.role === 'teacher' || user.role === 'admin')

  useEffect(() => {
    if (authLoading || !allowed) return
    setLoading(true); setError(null)
    api.teacher.attendance.roster(id, date)
      .then((d) => { setCourse(d.course); setRoster(d.roster || []) })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [authLoading, allowed, id, date])

  async function toggle(row) {
    setBusyId(row.enrollment_id); setError(null)
    const attended = Number(row.attended) === 1
    try {
      const call = attended ? api.teacher.attendance.remove : api.teacher.attendance.add
      const d = await call(id, { date, enrollment_ids: [row.enrollment_id] })
      setRoster(d.roster || [])
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusyId(null) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!allowed) {
    return <main className="container"><p className="error">{t('teacher.errors.not_teacher')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to={`/teacher/courses/${id}`}>← {course ? course.name : t('teacher.title')}</Link></p>
      <h1>{t('teacher.attendance_title', { course: course ? course.name : '' })}</h1>

      <label style={{ maxWidth: '16rem' }}>
        <span>{t('teacher.attendance_date')}</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>

      {error && <p className="error">{t(`teacher.errors.${error}`, { defaultValue: error })}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && roster.length === 0 && <p className="muted">{t('teacher.no_participants')}</p>}

      {!loading && roster.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('teacher.col_participant')}</th>
              <th>{t('teacher.col_present')}</th>
            </tr>
          </thead>
          <tbody>
            {roster.map((r) => (
              <tr key={r.enrollment_id}>
                <td>{r.participant_name}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={Number(r.attended) === 1}
                    disabled={busyId === r.enrollment_id}
                    onChange={() => toggle(r)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}
